/**
 * Authentication — OAuth 2.1 + PKCE browser login + way_ token fallback
 *
 * Two login paths:
 * 1. Browser login (default): OAuth 2.1 with PKCE → JWT + refresh token
 * 2. Token login (--token): User pastes a way_ scoped token directly
 *
 * The CLI exchanges codes directly with AuthKit's token endpoint.
 */

import * as crypto from 'node:crypto';
import * as http from 'node:http';
import { readConfig, writeConfig } from './config.js';
import type { CliConfig } from './config.js';
import { getToken, setOAuthTokens, clearTokens, migrateLegacyTokens } from './token-store.js';
import { setSentryUser } from './sentry.js';
import { maskSecretsInMessage } from './mask-secrets.js';

// =============================================================================
// PKCE Utilities
// =============================================================================

export function generateCodeVerifier(): string {
  return crypto.randomBytes(32).toString('base64url');
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const hash = crypto.createHash('sha256').update(verifier).digest();
  return hash.toString('base64url');
}

export function generateState(): string {
  return crypto.randomBytes(16).toString('base64url');
}

// =============================================================================
// AuthKit Token Exchange
// =============================================================================

/**
 * Convert a token lifetime in seconds (as returned by AuthKit) into an absolute
 * ISO 8601 timestamp suitable for `token_expires_at` in the CLI config.
 */
export function expiresInToIso(expiresInSeconds: number): string {
  return new Date(Date.now() + expiresInSeconds * 1000).toISOString();
}

export async function exchangeCodeForTokens(
  authkitDomain: string,
  clientId: string,
  code: string,
  codeVerifier: string,
  redirectUri: string,
): Promise<{ access_token: string; refresh_token?: string; expires_in: number }> {
  const response = await fetch(`${authkitDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      code_verifier: codeVerifier,
      redirect_uri: redirectUri,
      client_id: clientId,
    }).toString(),
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Token exchange failed (${response.status}): ${maskSecretsInMessage(body)}`);
  }

  return response.json() as Promise<{
    access_token: string;
    refresh_token?: string;
    expires_in: number;
  }>;
}

export async function refreshAccessToken(
  authkitDomain: string,
  clientId: string,
  refreshToken: string,
): Promise<{ access_token: string; refresh_token?: string; expires_in: number }> {
  const response = await fetch(`${authkitDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: clientId,
    }).toString(),
  });

  if (!response.ok) {
    const status = response.status;
    const body = await response.text();

    if (status === 400 || status === 401) {
      let isInvalidGrant = false;
      try {
        const parsed = JSON.parse(body);
        if (parsed.error === 'invalid_grant') isInvalidGrant = true;
      } catch { /* non-JSON body */ }
      if (isInvalidGrant) throw new Error('SESSION_EXPIRED');
    }

    throw new Error(`Token refresh failed (${status}): ${maskSecretsInMessage(body)}`);
  }

  return response.json() as Promise<{
    access_token: string;
    refresh_token?: string;
    expires_in: number;
  }>;
}

export async function validateToken(apiUrl: string, token: string): Promise<void> {
  const response = await fetch(`${apiUrl}/api/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Token validation failed (${response.status})`);
  }
}

// =============================================================================
// Callback Server
// =============================================================================

/** Fixed port for OAuth callback — must match the redirect URI registered in WorkOS */
export const OAUTH_CALLBACK_PORT = 3829;

export function startCallbackServer(
  port: number,
  expectedState: string,
  timeoutMs: number = 120_000
): Promise<{ code: string; port: number }> {
  return new Promise((resolve, reject) => {
    const server = http.createServer((req, res) => {
      const url = new URL(req.url || '/', `http://127.0.0.1:${port}`);

      if (url.pathname === '/callback') {
        const code = url.searchParams.get('code');
        const state = url.searchParams.get('state');
        const error = url.searchParams.get('error');
        const errorDescription = url.searchParams.get('error_description');

        if (error) {
          res.writeHead(400, { 'Content-Type': 'text/html' });
          res.end(`<html><body><h2>Login failed</h2><p>${errorDescription || error}</p></body></html>`);
          server.close();
          reject(new Error(`OAuth callback error: ${errorDescription || error}`));
          return;
        }

        if (state !== expectedState) {
          res.writeHead(400, { 'Content-Type': 'text/html' });
          res.end('<html><body><h2>Login failed</h2><p>Invalid state parameter</p></body></html>');
          server.close();
          reject(new Error('OAuth state mismatch — possible CSRF attack'));
          return;
        }

        if (code) {
          res.writeHead(200, { 'Content-Type': 'text/html' });
          res.end('<html><body><h2>Login successful!</h2><p>You can close this tab and return to your terminal.</p></body></html>');
          server.close();
          resolve({ code, port });
        } else {
          res.writeHead(400, { 'Content-Type': 'text/html' });
          res.end('<html><body><h2>Login failed</h2><p>No authorization code received</p></body></html>');
          server.close();
          reject(new Error('No authorization code received'));
        }
      } else {
        res.writeHead(404);
        res.end();
      }
    });

    const timeout = setTimeout(() => {
      server.close();
      reject(new Error('Timed out waiting for login. Run `wayai login` again.'));
    }, timeoutMs);

    server.on('close', () => clearTimeout(timeout));
    server.listen(port, '127.0.0.1');

    server.on('error', (err: NodeJS.ErrnoException) => {
      if (err.code === 'EADDRINUSE') {
        reject(new Error(`Port ${port} is in use. Try again.`));
      } else {
        reject(err);
      }
    });
  });
}

// =============================================================================
// Token Resolution (for API calls)
// =============================================================================

const AUTHKIT_DOMAIN = 'https://sprightly-comic-51.authkit.app';
const AUTHKIT_CLIENT_ID = 'client_01KMZHB50KENM5SV90ZA80VXK4'; // WayAI CLI Connect app (public, PKCE)

export function getAuthKitDomain(): string {
  return process.env.WAYAI_AUTHKIT_DOMAIN || AUTHKIT_DOMAIN;
}

export function getAuthKitClientId(): string {
  return process.env.WAYAI_AUTHKIT_CLIENT_ID || AUTHKIT_CLIENT_ID;
}

/**
 * Get a valid access token. Reads from the keyring (or env/file fallback) and
 * handles OAuth refresh transparently when nearing expiry.
 *
 * `config` is passed in for `api_url` access and to support the SESSION_EXPIRED
 * recovery path. Token data does NOT live in config anymore — it's resolved
 * from token-store.
 */
export async function getAccessToken(config: CliConfig): Promise<string> {
  // One-time migration from legacy plaintext config (versions < 0.4.0). Idempotent.
  await migrateLegacyTokens();

  const resolved = await getToken();
  if (!resolved) {
    throw new Error('No token found. Run `wayai login` to authenticate.');
  }

  // way_ token path — no refresh needed
  if (resolved.kind === 'way') {
    return resolved.token;
  }

  // OAuth JWT path — check expiry and refresh if needed
  const expiresAt = resolved.expires_at;
  if (!expiresAt) {
    return resolved.access_token;
  }

  if (new Date(expiresAt).getTime() > Date.now() + 60_000) {
    return resolved.access_token;
  }

  if (!resolved.refresh_token) {
    throw new Error('Access token expired and no refresh token available. Run `wayai login` again.');
  }

  // Token expired — refresh
  try {
    const result = await refreshAccessToken(
      getAuthKitDomain(),
      getAuthKitClientId(),
      resolved.refresh_token,
    );

    const newExpiresAt = expiresInToIso(result.expires_in);
    // setOAuthTokens persists token_expires_at to the metadata file — no need to
    // writeConfig again here (would double-write the same file).
    await setOAuthTokens(result.access_token, result.refresh_token ?? resolved.refresh_token, newExpiresAt);
    return result.access_token;
  } catch (err) {
    if (err instanceof Error && err.message === 'SESSION_EXPIRED') {
      await clearTokens();
      writeConfig({ api_url: config.api_url });
      throw new Error('Session expired. Please run `wayai login` again.');
    }
    throw err;
  }
}

export async function requireAuth(): Promise<{ config: CliConfig; accessToken: string }> {
  const config = readConfig();
  if (!config) {
    console.error('Not logged in. Run `wayai login` first.');
    process.exit(1);
  }

  try {
    const accessToken = await getAccessToken(config);
    setSentryUser(accessToken);
    return { config, accessToken };
  } catch (err) {
    console.error(`Authentication failed: ${err instanceof Error ? err.message : String(err)}`);
    console.error('Run `wayai login` to re-authenticate.');
    process.exit(1);
  }
}
